import { useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  acceptSuggestion,
  requestCancellation,
  fetchVisitorRequests,
  fetchVisitorSession,
  withdrawRequest,
} from "../api.js";
import { Lockup } from "../components/Logo.js";

/**
 * A visitor's own requests (2k, 2q).
 *
 * Statuses use the shared vocabulary, with the qualifier naming whoever has to
 * act next - so "Requested · awaiting Samer" here is the same state the owner
 * sees as "Requested · awaiting you".
 */
const LABELS: Record<string, string> = {
  requested: "Requested",
  suggested: "Suggested",
  confirmed: "Confirmed",
  cancellation_requested: "Cancellation requested",
  declined: "Declined",
  expired: "Expired",
  withdrawn: "Withdrawn",
  cancelled: "Cancelled",
};

const OPEN = ["requested", "suggested", "confirmed", "cancellation_requested"];

export function VisitorRequests() {
  const queryClient = useQueryClient();
  const zone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone, []);
  const session = useQuery({ queryKey: ["visitor"], queryFn: fetchVisitorSession });
  const requests = useQuery({
    queryKey: ["visitor-requests"],
    queryFn: fetchVisitorRequests,
  });

  const accept = useMutation({
    mutationFn: ({ id, startUtc }: { id: string; startUtc: string }) =>
      acceptSuggestion(id, startUtc),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["visitor-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["visitor-availability"] });
    },
  });

  const askToCancel = useMutation({
    mutationFn: (id: string) => requestCancellation(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["visitor-requests"] }),
  });

  const withdraw = useMutation({
    mutationFn: withdrawRequest,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["visitor-requests"] });
      void queryClient.invalidateQueries({ queryKey: ["visitor-availability"] });
    },
  });

  const ownerName = session.data?.visitor?.ownerName ?? "your host";
  const all = requests.data?.requests ?? [];
  const open = all.filter((request) => OPEN.includes(request.status));
  const earlier = all.filter((request) => !OPEN.includes(request.status));

  const when = (iso: string) =>
    new Date(iso).toLocaleString(undefined, {
      timeZone: zone,
      weekday: "short",
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });

  const row = (request: (typeof all)[number]) => (
    <article key={request.id} className="request">
      <header className="request-head">
        <div className="request-when">
          <strong>{when(request.startUtc)}</strong>
          <span className="helper">
            {request.durationMinutes} min · {request.purpose}
          </span>
        </div>
        <span
          className={request.status === "confirmed" ? "status" : "status status-warn"}
        >
          {LABELS[request.status] ?? request.status}
          {request.status === "requested" ? ` · awaiting ${ownerName}` : ""}
          {request.status === "suggested" ? " · awaiting you" : ""}
        </span>
      </header>

      {request.status === "suggested" && request.suggestions.length > 0 ? (
        <div className="suggest">
          <p className="helper">
            {ownerName} suggested these instead. Accepting one confirms the meeting
            straight away — there&rsquo;s no second round.
          </p>
          <div className="row">
            {request.suggestions.map((suggestion) => (
              <button
                key={suggestion.startUtc}
                type="button"
                className="secondary"
                disabled={accept.isPending}
                onClick={() =>
                  accept.mutate({ id: request.id, startUtc: suggestion.startUtc })
                }
              >
                {when(suggestion.startUtc)}
              </button>
            ))}
            <button
              type="button"
              className="ghost"
              onClick={() => withdraw.mutate(request.id)}
              disabled={withdraw.isPending}
            >
              None of these work
            </button>
          </div>
          {accept.isError ? (
            <div className="notice notice-error" role="alert">
              <strong>That didn&rsquo;t work</strong>
              <span>{accept.error.message}</span>
            </div>
          ) : null}
        </div>
      ) : null}

      {request.status === "confirmed" ? (
        <div className="row">
          <span className="helper">
            Need to cancel? {ownerName} decides — the meeting stands until they do.
          </span>
          <button
            type="button"
            className="ghost"
            onClick={() => askToCancel.mutate(request.id)}
            disabled={askToCancel.isPending}
          >
            Request cancellation
          </button>
        </div>
      ) : null}

      {request.status === "cancellation_requested" ? (
        <p className="helper">
          You asked to cancel this. {ownerName} hasn&rsquo;t answered yet, so it&rsquo;s
          still on.
        </p>
      ) : null}

      {request.status === "requested" ? (
        <div className="row">
          <span className="helper">
            Expires {when(request.expiresAt)} if {ownerName} hasn&rsquo;t answered.
          </span>
          <button
            type="button"
            className="ghost"
            onClick={() => withdraw.mutate(request.id)}
            disabled={withdraw.isPending}
          >
            Withdraw
          </button>
        </div>
      ) : null}
    </article>
  );

  return (
    <main className="visitor">
      <header className="visitor-head">
        <Lockup size={20} />
        <Link to="/book" className="helper">
          Pick another time
        </Link>
      </header>

      <h1>My requests</h1>
      <p className="lede">
        Only requests from {session.data?.visitor?.email ?? "you"} to {ownerName}.
      </p>

      {requests.isPending ? <p className="helper">Loading…</p> : null}
      {!requests.isPending && all.length === 0 ? (
        <p className="helper">Nothing requested yet.</p>
      ) : null}

      {open.map(row)}
      {earlier.length > 0 ? <h2>Earlier</h2> : null}
      {earlier.map(row)}
    </main>
  );
}
